// ── BCH Live — Tips, Goals & Supporters ───────────────────────────
//
// One hook owns everything money-shaped for a single stream, so the
// player overlays, the Super Chat feed, the supporters bar and the goal
// bar all read from the same normalised list instead of each running
// their own query and parsing receipts differently.
//
// Signals consumed:
//   • kind 9735 tip receipts tagged `bch-tip` and `#p` the creator
//   • kind 30078 creator goal (`bchnostr/creator-goal`), author-filtered
//   • kind 3 contact lists, to detect brand-new followers mid-stream
//
// Trust note: the goal is an addressable event, so a `d` tag alone is
// NOT a trust boundary — we always filter by `authors: [creatorPubkey]`
// so nobody else can inject a goal onto someone's stream.

import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import type { Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { getBCHPrice } from './useBCHPrice';
import {
  GOAL_KIND, GOAL_TAG, type CreatorGoalData,
} from '../lib/creatorSupport';
import type { TipEntry } from '../components/live/StreamOverlays';

export const TIP_RECEIPT_KIND = 9735;

/** Amount that earns a full-screen highlight card over the player. */
export const HIGHLIGHT_MIN_USD = 1;

// ══════════════════════════════════════════════════════════════════
//  RECEIPT PARSING
// ══════════════════════════════════════════════════════════════════

/**
 * Normalise a kind 9735 receipt into sats + USD + message.
 *
 * Receipts are written by several code paths over the app's life, so we
 * accept JSON content, a plain-text message, and/or an `amount` tag, and
 * back-fill whichever of sats/USD is missing from the current BCH price.
 */
function parseTipReceipt(event: Event): { sats: number; usd: number; message: string } {
  let sats = 0;
  let usd = 0;
  let message = '';

  try {
    const d = JSON.parse(event.content) as {
      amount_sats?: number; amount_usd?: number; message?: string;
    };
    if (typeof d.amount_sats === 'number') sats = d.amount_sats;
    if (typeof d.amount_usd === 'number') usd = d.amount_usd;
    if (typeof d.message === 'string') message = d.message;
  } catch {
    // Not JSON — older receipts put the message straight in content.
    message = typeof event.content === 'string' ? event.content : '';
  }

  if (!sats) {
    const amt = event.tags.find(t => t[0] === 'amount')?.[1];
    if (amt) {
      const n = parseInt(amt, 10);
      if (Number.isFinite(n)) sats = n;
    }
  }

  const price = getBCHPrice();
  if (!usd && sats > 0 && price) usd = (sats / 1e8) * price;
  if (!sats && usd > 0 && price) sats = Math.round((usd / price) * 1e8);

  return { sats, usd, message: message.trim() };
}

function toTipEntry(event: Event): TipEntry {
  const { sats, usd, message } = parseTipReceipt(event);
  return { id: event.id, pubkey: event.pubkey, usd, sats, message, created_at: event.created_at };
}

// ══════════════════════════════════════════════════════════════════
//  RESULT SHAPE
// ══════════════════════════════════════════════════════════════════

export interface StreamGoal {
  title: string;
  description: string;
  targetUsd: number;
  raisedUsd: number;
  pct: number;
  complete: boolean;
}

export interface UseStreamTipsResult {
  /** Newest first, every tip in the window */
  tips: TipEntry[];
  /** Tips that arrived since mount, newest first (drives animations) */
  liveTips: TipEntry[];
  /** Set of ids that arrived since mount, for "new" styling */
  newIds: Set<string>;
  /** Aggregated per-supporter totals, biggest first */
  supporters: Array<{ pubkey: string; usd: number; count: number }>;
  totalUsd: number;
  totalSats: number;
  /** Active donation goal, with progress folded in */
  goal: StreamGoal | null;
  /** Most recent tip worth a full-screen highlight, or null */
  highlight: TipEntry | null;
  clearHighlight: () => void;
  /** Most recent new follower detected mid-stream, or null */
  newFollower: string | null;
  clearFollower: () => void;
  refetch: () => void;
}

// ══════════════════════════════════════════════════════════════════
//  HOOK
// ══════════════════════════════════════════════════════════════════

export function useStreamTips(
  creatorPubkey: string | null | undefined,
  opts: { watchFollowers?: boolean } = {},
): UseStreamTipsResult {
  const { watchFollowers = false } = opts;
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400);

  // ── Tip receipts ──
  const tipFilters = useMemo(
    () => (creatorPubkey
      ? [{ kinds: [TIP_RECEIPT_KIND], '#p': [creatorPubkey], '#t': ['bch-tip'], limit: 200, since }]
      : []),
    [creatorPubkey, since],
  );

  const { events: tipEventsRaw, refetch } = useNostrQuery({
    filters: tipFilters,
    enabled: !!creatorPubkey,
  });

  // ── Donation goal (author-filtered: a `d` tag is not a trust boundary) ──
  const goalFilters = useMemo(
    () => (creatorPubkey
      ? [{ kinds: [GOAL_KIND], authors: [creatorPubkey], '#t': [GOAL_TAG], limit: 1 }]
      : []),
    [creatorPubkey],
  );

  const { events: goalEvents } = useNostrQuery({
    filters: goalFilters,
    enabled: !!creatorPubkey,
    allRelays: true,
  });

  // ── Followers, only when a creator asked for alert overlays ──
  const followerFilters = useMemo(
    () => (watchFollowers && creatorPubkey
      ? [{ kinds: [3], '#p': [creatorPubkey], limit: 200, since }]
      : []),
    [watchFollowers, creatorPubkey, since],
  );

  const { events: followerEvents } = useNostrQuery({
    filters: followerFilters,
    enabled: watchFollowers && !!creatorPubkey,
  });

  // ── Normalise + sort ──
  const tipEvents = useMemo(
    () => [...tipEventsRaw].sort((a, b) => b.created_at - a.created_at),
    [tipEventsRaw],
  );

  const tips = useMemo(() => tipEvents.map(toTipEntry), [tipEvents]);

  // ── New-arrival tracking ──
  //
  // The first batch to land is historical. Marking it seen without firing
  // animations is what stops every visitor getting a wall of old Super
  // Chats exploding across the player on arrival.
  const seenRef = useRef<Set<string>>(new Set());
  const primedRef = useRef(false);
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [liveTips, setLiveTips] = useState<TipEntry[]>([]);
  const [highlight, setHighlight] = useState<TipEntry | null>(null);

  useEffect(() => {
    if (tips.length === 0) return;

    if (!primedRef.current) {
      primedRef.current = true;
      for (const t of tips) seenRef.current.add(t.id);
      return;
    }

    const fresh: TipEntry[] = [];
    for (const t of tips) {
      if (seenRef.current.has(t.id)) continue;
      seenRef.current.add(t.id);
      fresh.push(t);
    }
    if (fresh.length === 0) return;

    setLiveTips(prev => [...fresh, ...prev].slice(0, 30));
    setNewIds(prev => {
      const next = new Set(prev);
      for (const t of fresh) next.add(t.id);
      return next;
    });

    // Biggest of the batch gets the highlight card.
    const biggest = fresh.reduce((a, b) => (b.usd > a.usd ? b : a), fresh[0]);
    if (biggest.usd >= HIGHLIGHT_MIN_USD) setHighlight(biggest);

    const ids = fresh.map(t => t.id);
    const timer = setTimeout(() => {
      setNewIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }, 2500);
    return () => clearTimeout(timer);
  }, [tips]);

  const clearHighlight = useCallback(() => setHighlight(null), []);

  // ── New follower detection ──
  const knownFollowersRef = useRef<Set<string> | null>(null);
  const [newFollower, setNewFollower] = useState<string | null>(null);

  useEffect(() => {
    if (!watchFollowers || !creatorPubkey || followerEvents.length === 0) return;

    const current = new Set<string>();
    for (const ev of followerEvents) {
      if (ev.tags.some(t => t[0] === 'p' && t[1] === creatorPubkey)) current.add(ev.pubkey);
    }

    // First snapshot is the baseline, not an alert storm.
    if (knownFollowersRef.current === null) {
      knownFollowersRef.current = current;
      return;
    }

    for (const pk of current) {
      if (!knownFollowersRef.current.has(pk)) {
        knownFollowersRef.current.add(pk);
        setNewFollower(pk);
        break; // one alert at a time; the overlay dismisses itself
      }
    }
  }, [followerEvents, watchFollowers, creatorPubkey]);

  const clearFollower = useCallback(() => setNewFollower(null), []);

  // ── Aggregates ──
  const { supporters, totalUsd, totalSats } = useMemo(() => {
    const byPk = new Map<string, { usd: number; count: number }>();
    let usdSum = 0;
    let satsSum = 0;

    for (const t of tips) {
      const entry = byPk.get(t.pubkey) || { usd: 0, count: 0 };
      entry.usd += t.usd;
      entry.count += 1;
      byPk.set(t.pubkey, entry);
      usdSum += t.usd;
      satsSum += t.sats;
    }

    const sorted = [...byPk.entries()]
      .map(([pubkey, v]) => ({ pubkey, usd: v.usd, count: v.count }))
      .sort((a, b) => b.usd - a.usd);

    return { supporters: sorted, totalUsd: usdSum, totalSats: satsSum };
  }, [tips]);

  // ── Goal ──
  const goal = useMemo<StreamGoal | null>(() => {
    if (goalEvents.length === 0) return null;
    const ev = [...goalEvents].sort((a, b) => b.created_at - a.created_at)[0];
    let data: CreatorGoalData;
    try {
      data = JSON.parse(ev.content) as CreatorGoalData;
    } catch {
      return null;
    }
    if (!data.active || !data.targetUsd || data.targetUsd <= 0) return null;

    // Only tips received while this goal has been active count toward it,
    // otherwise a creator's whole tipping history instantly completes it.
    const raisedUsd = tips
      .filter(t => t.created_at >= (data.createdAt || ev.created_at))
      .reduce((sum, t) => sum + t.usd, 0);

    const pct = Math.min(100, Math.round((raisedUsd / data.targetUsd) * 100));
    return {
      title: data.title || 'Donation goal',
      description: data.description || '',
      targetUsd: data.targetUsd,
      raisedUsd,
      pct,
      complete: pct >= 100,
    };
  }, [goalEvents, tips]);

  // Keep the feed warm while the stream is open.
  useEffect(() => {
    if (!creatorPubkey) return;
    const i = setInterval(refetch, 15_000);
    return () => clearInterval(i);
  }, [creatorPubkey, refetch]);

  return {
    tips,
    liveTips,
    newIds,
    supporters,
    totalUsd,
    totalSats,
    goal,
    highlight,
    clearHighlight,
    newFollower,
    clearFollower,
    refetch,
  };
}
